/**
 * Authentication Service
 * Handles password reset and authentication-related API calls
 * 
 * Note: Direct endpoint definitions for auth module independence
 */

import { nodeApi } from "@shared/api/apiClient";

/**
 * Send password reset link to user email
 * @param {Object} params - Request parameters
 * @param {string} params.globalAccessToken - Global access token
 * @param {string} params.emailId - User email address
 * @returns {Promise<Object>} Response from forgot password API
 */
export const forgotPasswordApi = async ({ globalAccessToken, emailId }) => {
  try {
    const response = await nodeApi.post(
      '/user/forgot-password',
      { emailId },
      {
        headers: {
          Authorization: `Bearer ${globalAccessToken}`,
        },
      }
    );
    
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      data: error.message || "Failed to send reset link",
    };
  }
};

/**
 * Login user with credentials
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.username - User email
 * @param {string} credentials.password - User password
 * @returns {Promise<Object>} Response from login API
 */
export const loginApi = async (credentials) => {
  try {
    const response = await nodeApi.post(
      '/user/get-user-access-token',
      credentials
    );
    
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      data: error.message || "Login failed",
      status: error.status,
    };
  }
};

/**
 * Refresh access token
 * @param {Object} params - Request parameters  
 * @param {string} params.username - User email
 * @returns {Promise<Object>} Response from token refresh API
 */
export const refreshTokenApi = async ({ username }) => {
  try {
    const response = await nodeApi.post(
      '/user/get-salesforce-access-token',
      { username }
    );
    
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    return {
      success: false,
      data: error.message || "Token refresh failed",
      status: error.status,
    };
  }
};
